import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PromptResearchCreateRequest {
  research_type: 'seo_keywords' | 'url_analysis' | 'brand_brainstorm';
  input_data: {
    keywords?: string[];
    url?: string;
    brand_info?: {
      brand_name?: string;
      industry?: string;
      products?: string[];
      target_audience?: string;
    };
  };
  language?: string;
  country?: string;
  project_id?: string;
}

export interface GeneratedPrompt {
  text: string;
  relevance_score: number;
  monthly_intent_volume: number;
}

export interface PromptResearch {
  id: string;
  research_name: string;
  research_type: string;
  language: string;
  country: string;
  prompts: GeneratedPrompt[];
  prompt_count: number;
  created_at: string;
  updated_at: string;
}

export interface PromptResearchListResponse {
  researches: PromptResearch[];
  total: number;
}

/** Response when starting a prompt research create (task-based). Poll status with task_id. */
export interface PromptResearchCreateTaskResponse {
  task_id: string;
}

/** Status of a prompt research create task (for polling). */
export interface PromptResearchCreateTaskStatus {
  task_id: string;
  status: 'pending' | 'progress' | 'completed' | 'failed';
  research_id?: string;
  error_message?: string;
  progress_percentage?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PromptResearchService {
  private apiUrl = `${environment.apiUrl}/api/prompt-research`;

  constructor(private http: HttpClient) {}

  /** Start prompt research create (task-based). Returns task_id; poll getCreateStatus until completed. */
  createResearch(request: PromptResearchCreateRequest): Observable<PromptResearchCreateTaskResponse> {
    return this.http.post<PromptResearchCreateTaskResponse>(`${this.apiUrl}/create`, request);
  }

  /** Get status of a prompt research create task. Poll until status is 'completed' or 'failed'. */
  getCreateStatus(taskId: string): Observable<PromptResearchCreateTaskStatus> {
    return this.http.get<PromptResearchCreateTaskStatus>(`${this.apiUrl}/create/status/${taskId}`);
  }

  getResearchList(projectId?: string): Observable<PromptResearchListResponse> {
    let params = new HttpParams();
    if (projectId) {
      params = params.set('project_id', projectId);
    }
    return this.http.get<PromptResearchListResponse>(`${this.apiUrl}/list`, { params });
  }

  getResearchById(researchId: string): Observable<PromptResearch> {
    return this.http.get<PromptResearch>(`${this.apiUrl}/${researchId}`);
  }

  exportResearch(researchId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${researchId}/export`, {
      responseType: 'blob'
    });
  }

  /** Delete entire research (all prompts under that research name). */
  deleteResearch(researchId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/${researchId}`);
  }
}

